import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Building2, Mail, Lock, User, Phone, MapPin } from 'lucide-react';
import AddressAutocomplete from '../components/AddressAutocomplete';

export default function RegisterCompanyPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    company: '',
    name: '',
    firstname: '',
    siret: '',
    address: '',
    postal_code: '',
    city: '',
    phone: '',
    email: '',
    password: '',
    is_company: true
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (formData.password.length < 6) {
      toast.error('Le mot de passe doit contenir au moins 6 caractères');
      return;
    }
    if (!formData.company) {
      toast.error('Le nom de la entreprise est obligatoire');
      return;
    }
    if (!formData.name || !formData.firstname) {
      toast.error('Le nom et prénom de l\'interlocuteur sont obligatoires');
      return;
    }
    if (!formData.siret || formData.siret.length < 14) {
      toast.error('Le numéro SIRET doit contenir 14 chiffres');
      return;
    }
    if (!formData.address || !formData.postal_code || !formData.city) {
      toast.error('L\'adresse complète est obligatoire');
      return;
    }
    if (!formData.phone) {
      toast.error('Le numéro de téléphone est obligatoire');
      return;
    }
    
    setLoading(true);
    try {
      await register(formData);
      toast.success('Compte entreprise créé avec succès !');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  // Format SIRET with spaces
  const formatSiret = (value) => {
    const numbers = value.replace(/\D/g, '').slice(0, 14);
    return numbers;
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-2xl bg-zinc-900/50 border-zinc-800">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-orange-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="text-orange-500" size={32} />
          </div>
          <CardTitle className="text-2xl text-white">Créer un compte Entreprise</CardTitle>
          <CardDescription className="text-zinc-400">
            Inscrivez votre entreprise et profitez de nos services professionnels
          </CardDescription>
          
          {/* Toggle between Particulier and Entreprise */}
          <div className="flex justify-center gap-2 mt-4">
            <Link to="/inscription">
              <Button variant="outline" className="border-zinc-700 text-zinc-400 hover:bg-zinc-800">
                <User size={16} className="mr-2" />
                Particulier
              </Button>
            </Link>
            <Button className="bg-orange-600 hover:bg-orange-700">
              <Building2 size={16} className="mr-2" />
              Entreprise
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nom de entreprise */}
            <div>
              <Label className="text-zinc-300">Nom de la entreprise <span className="text-red-500">*</span></Label>
              <div className="relative mt-2">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  required
                  placeholder="Nom de votre entreprise"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white pl-10"
                  data-testid="register-company-name"
                />
              </div>
            </div>

            {/* Nom et Prénom de l'interlocuteur */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-zinc-300">Nom de l'interlocuteur <span className="text-red-500">*</span></Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                  <Input
                    required
                    placeholder="Nom"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white pl-10"
                    data-testid="register-company-contact-name"
                  />
                </div>
              </div>
              <div>
                <Label className="text-zinc-300">Prénom de l'interlocuteur <span className="text-red-500">*</span></Label>
                <div className="relative mt-2">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                  <Input
                    required
                    placeholder="Prénom"
                    value={formData.firstname}
                    onChange={(e) => setFormData({...formData, firstname: e.target.value})}
                    className="bg-zinc-950 border-zinc-800 text-white pl-10"
                    data-testid="register-company-contact-firstname"
                  />
                </div>
              </div>
            </div>

            {/* SIRET */}
            <div>
              <Label className="text-zinc-300">Numéro SIRET <span className="text-red-500">*</span></Label>
              <div className="relative mt-2">
                <Input
                  required
                  placeholder="12345678901234"
                  value={formData.siret}
                  onChange={(e) => setFormData({...formData, siret: formatSiret(e.target.value)})}
                  className="bg-zinc-950 border-zinc-800 text-white"
                  maxLength={14}
                  data-testid="register-company-siret"
                />
                <span className="text-zinc-500 text-xs mt-1 block">14 chiffres</span>
              </div>
            </div>

            {/* Adresse avec autocomplete BAN */}
            <div>
              <Label className="text-zinc-300">Adresse <span className="text-red-500">*</span> <span className="text-zinc-500 text-xs">(saisie assistee)</span></Label>
              <div className="mt-2">
                <AddressAutocomplete
                  value={formData.address}
                  onChange={(v) => setFormData((p) => ({ ...p, address: v }))}
                  onSelect={(s) => setFormData((p) => ({
                    ...p,
                    address: s.address || s.label,
                    postal_code: s.postcode || p.postal_code,
                    city: s.city || p.city,
                  }))}
                  required
                  placeholder="Ex : 142 boulevard Murat, 75016 Paris"
                  testid="register-company-address"
                />
              </div>
            </div>

            {/* Code postal */}
            <div>
              <Label className="text-zinc-300">Code postal <span className="text-red-500">*</span></Label>
              <div className="relative mt-2">
                <Input
                  required
                  placeholder="75000"
                  value={formData.postal_code}
                  onChange={(e) => setFormData({...formData, postal_code: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white"
                  data-testid="register-company-postalcode"
                />
              </div>
            </div>

            {/* Ville */}
            <div>
              <Label className="text-zinc-300">Ville <span className="text-red-500">*</span></Label>
              <div className="relative mt-2">
                <Input
                  required
                  placeholder="Ville"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white"
                  data-testid="register-company-city"
                />
              </div>
            </div>

            {/* Téléphone */}
            <div>
              <Label className="text-zinc-300">Numéro de téléphone <span className="text-red-500">*</span></Label>
              <div className="relative mt-2">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  required
                  type="tel"
                  placeholder="01 23 45 67 89"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white pl-10"
                  data-testid="register-company-phone"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <Label className="text-zinc-300">Adresse email <span className="text-red-500">*</span></Label>
              <div className="relative mt-2">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  required
                  type="email"
                  placeholder="contact@societe.com"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white pl-10"
                  data-testid="register-company-email"
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <Label className="text-zinc-300">Mot de passe <span className="text-red-500">*</span></Label>
              <div className="relative mt-2">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
                <Input
                  required
                  type="password"
                  placeholder="Min. 6 caractères"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="bg-zinc-950 border-zinc-800 text-white pl-10"
                  data-testid="register-company-password"
                />
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-orange-600 hover:bg-orange-700 h-12 mt-6"
              disabled={loading}
              data-testid="register-company-submit"
            >
              {loading ? 'Création...' : 'Créer le compte entreprise'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-zinc-500">
              Déjà un compte ?{' '}
              <Link to="/connexion" className="text-red-500 hover:underline">
                Se connecter
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
